import { Navigate, Route, Routes } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AuthGate, FullScreenSpinner } from "@/components/auth-gate";
import { AppLayout } from "@/components/layout/app-layout";
import { resolveStatusHost } from "@/lib/api";
import { queryKeys } from "@/lib/queries";
import DashboardPage from "@/pages/dashboard";
import LoginPage from "@/pages/login";
import MaintenancePage from "@/pages/maintenance";
import MonitorDetailPage from "@/pages/monitor-detail";
import MonitorFormPage from "@/pages/monitor-form";
import NotificationsPage from "@/pages/notifications";
import PublicStatusPage from "@/pages/public-status";
import SettingsPage from "@/pages/settings";
import SetupPage from "@/pages/setup";
import StatusPageEditPage from "@/pages/status-page-edit";
import StatusPagesPage from "@/pages/status-pages";

/**
 * Root route: when served from a status page's custom domain, render that
 * status page at "/" directly (no redirect); otherwise go to the dashboard.
 */
function RootGate() {
  const host = window.location.host;
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.resolveHost(host),
    queryFn: () => resolveStatusHost(host),
    staleTime: 5 * 60_000,
    retry: 1,
  });
  if (isLoading) return <FullScreenSpinner />;
  if (data?.slug) return <PublicStatusPage slugOverride={data.slug} />;
  return <Navigate to="/dashboard" replace />;
}

function NotFound() {
  return (
    <div className="flex h-dvh flex-col items-center justify-center gap-2 bg-background">
      <p className="text-4xl font-bold">404</p>
      <p className="text-sm text-muted-foreground">This page does not exist.</p>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<RootGate />} />
      <Route path="/setup" element={<SetupPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/status/:slug" element={<PublicStatusPage />} />
      <Route
        element={
          <AuthGate>
            <AppLayout />
          </AuthGate>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/monitors/new" element={<MonitorFormPage />} />
        <Route path="/monitors/:id" element={<MonitorDetailPage />} />
        <Route path="/monitors/:id/edit" element={<MonitorFormPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/status-pages" element={<StatusPagesPage />} />
        <Route path="/status-pages/new" element={<StatusPageEditPage />} />
        <Route path="/status-pages/:id" element={<StatusPageEditPage />} />
        <Route path="/maintenance" element={<MaintenancePage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
